import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { endpoint } from "../../utils/endpoints";
import {
  downloadCsvLogFile,
  fetchDriverLedgerCsvLog,
  getErrorMessage,
  isCsvLogDownloadReady,
  parseBody,
  requestDriverLedgerCsvExport,
  ACCOUNTANT_API,
} from "../../utils/driverLedgerCsv";
import { hasCsvLinkInResponse } from "../../utils/csvExportSocket";
import { useCsvExportSocket } from "../../context/CsvExportSocketContext";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import DateRangeAppliedLabel from "../../Components/DateRangeFilter/DateRangeAppliedLabel";
import { parseApiDate, toApiDate, validateDateRange } from "../../utils/dateRange";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownloadIcon from "../../assets/image/download.svg";
import CsvLogStatusBadge from "../../Components/CsvLogStatusBadge/CsvLogStatusBadge";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatInr as formatINR, formatLedgerAmount } from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const C = {
  bg: "#FFFFFF",
  panel: "#FFFFFF",
  soft: "#FAFAFA",
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  tableHead: "#000053",
};

const ALL_TYPES = "All Types";

const downloadBlobFile = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const mapLedgerRow = (item) => ({
  id: item._id,
  dateTime: formatDateTime(item.created_at),
  verificationType: item.type ?? "—",
  tripId:
    item.trip_ref_id != null && item.trip_ref_id !== ""
      ? `${item.trip_ref_id}`
      : "—",
  credit: formatLedgerAmount(item.credit),
  debit: formatLedgerAmount(item.debit),
  runningBalance: formatLedgerAmount(item.balance),
  description: item.description ?? "—",
  createdAt: item.created_at ? new Date(item.created_at) : null,
});

function SalaryAwardsDetail() {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backToList = useBackToList("/salary-disbursement");
  const { trackExport, clearExport, registerExportHandler, isExportPending, finishExport } =
    useCsvExportSocket();
  const driverId = searchParams.get("driver_id") || "";
  const exportKey = driverId ? `ledger:${driverId}` : "";
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam === "logCsv" ? "logCsv" : "listing");

  useEffect(() => {
    if (tabParam === "logCsv") {
      setActiveTab("logCsv");
    }
  }, [tabParam]);

  useEffect(() => {
    setSubheading("View Salary & Awards Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pickerRange, setPickerRange] = useState([null, null]);
  const [transactionType, setTransactionType] = useState(ALL_TYPES);
  const [ledgerTypeOptions, setLedgerTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const dateRangeCheck = validateDateRange(fromDate, toDate);
  const dateValidationError = dateRangeCheck.valid ? "" : dateRangeCheck.message;
  const hasDateRangeSelected = Boolean(fromDate && toDate);

  const transactionTypeOptions = useMemo(
    () => [ALL_TYPES, ...ledgerTypeOptions],
    [ledgerTypeOptions]
  );

  const [loading, setLoading] = useState(true);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [driverFullName, setDriverFullName] = useState("—");
  const [currentBalance, setCurrentBalance] = useState(0);
  const [totalSalary, setTotalSalary] = useState(0);
  const [totalAward, setTotalAward] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [csvLogRows, setCsvLogRows] = useState([]);
  const [csvLogLoading, setCsvLogLoading] = useState(false);
  const [downloadingLogId, setDownloadingLogId] = useState("");
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    setPickerRange([parseApiDate(fromDate), parseApiDate(toDate)]);
  }, [fromDate, toDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, transactionType]);

  const fetchLedger = useCallback(
    async ({ useDateFilter = false, page = currentPage } = {}) => {
    if (!driverId) return;
    const requestId = ++fetchRequestIdRef.current;

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      if (requestId === fetchRequestIdRef.current) {
        setLedgerRows([]);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(itemsPerPage),
      driver_id: driverId,
    });
    if (transactionType && transactionType !== ALL_TYPES) {
      params.set("type", transactionType);
    }
    if (useDateFilter) {
      params.set("from_date", fromDate);
      params.set("to_date", toDate);
    }
    const url = `${ACCOUNTANT_API}${endpoint.listOfDriverLedger}?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await parseBody(response);
      if (requestId !== fetchRequestIdRef.current) return;

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        setLedgerRows([]);
        setTotalRecords(0);
        setApiTotalPages(1);
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        setLedgerRows([]);
        setTotalRecords(0);
        setApiTotalPages(1);
        return;
      }

      const data = payload?.data || {};
      const list = Array.isArray(data.driver_ledger_data) ? data.driver_ledger_data : [];

      setDriverFullName(data.driver_full_name ?? "—");
      setCurrentBalance(Number(data.current_balance) || 0);
      setTotalSalary(Number(data.total_salary) || 0);
      setTotalAward(Number(data.total_award) || 0);
      setLedgerRows(list.map(mapLedgerRow));
      const totalFromApi = Number(data.total);
      const total =
        Number.isFinite(totalFromApi) && totalFromApi >= 0 ? totalFromApi : list.length;
      const totalPagesFromApi =
        typeof data.total_pages === "number" && data.total_pages > 0 ? data.total_pages : 1;
      setTotalRecords(total);
      setApiTotalPages(totalPagesFromApi);
      if (page > totalPagesFromApi) {
        setCurrentPage(totalPagesFromApi);
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not load driver ledger."
      );
      setLedgerRows([]);
      setTotalRecords(0);
      setApiTotalPages(1);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  },
    [driverId, itemsPerPage, transactionType, fromDate, toDate]
  );

  useEffect(() => {
    let cancelled = false;

    const loadLedgerTypes = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        if (!cancelled) {
          toast.error("Session expired. Please log in again.");
          setTypesLoading(false);
        }
        return;
      }

      setTypesLoading(true);
      try {
        const response = await fetch(`${ACCOUNTANT_API}${endpoint.listOfLedgerType}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setLedgerTypeOptions([]);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setLedgerTypeOptions([]);
          return;
        }

        const list = Array.isArray(payload?.data) ? payload.data : [];
        setLedgerTypeOptions(
          list.map((item) => item?.name).filter((name) => typeof name === "string" && name.trim())
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load transaction types."
          );
          setLedgerTypeOptions([]);
        }
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    };

    loadLedgerTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!driverId) {
      toast.error("Please select a driver from Salary Disbursement.");
      navigate(backToList, { replace: true });
      setLoading(false);
      return undefined;
    }

    if (dateValidationError) {
      setLedgerRows([]);
      setLoading(false);
      setTotalRecords(0);
      setApiTotalPages(1);
      return undefined;
    }

    fetchLedger({
      useDateFilter: hasDateRangeSelected,
      page: currentPage,
    });
    return undefined;
  }, [
    driverId,
    navigate,
    fetchLedger,
    fromDate,
    toDate,
    transactionType,
    currentPage,
    dateValidationError,
    hasDateRangeSelected,
  ]);

  const totalPages = apiTotalPages;
  const page = Math.min(currentPage, totalPages);
  const firstIndex = (page - 1) * itemsPerPage;
  const currentRows = ledgerRows;
  const hasBothDates = hasDateRangeSelected;
  const exportInProgress = exporting || (exportKey && isExportPending(exportKey));
  const canExport =
    !exportInProgress &&
    !dateValidationError &&
    !loading &&
    totalRecords > 0;
  const canPaginate = !loading && !dateValidationError;

  const fetchCsvLog = useCallback(async () => {
    if (!driverId) return;

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setCsvLogLoading(true);
    try {
      const rows = await fetchDriverLedgerCsvLog(driverId, token);
      setCsvLogRows(rows);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : err?.message || "Could not load CSV log."
      );
      setCsvLogRows([]);
    } finally {
      setCsvLogLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    if (activeTab === "logCsv" && driverId) {
      fetchCsvLog();
    }
  }, [activeTab, driverId, fetchCsvLog]);

  useEffect(() => {
    if (!exportKey) return undefined;

    return registerExportHandler(exportKey, {
      onComplete: () => {
        fetchCsvLog();
        setActiveTab("logCsv");
        setExporting(false);
      },
      onError: () => setExporting(false),
    });
  }, [exportKey, registerExportHandler, fetchCsvLog]);

  const handleApplyDates = () => {
    const [start, end] = pickerRange;
    if (!start || !end) {
      toast.error("Please select start and end dates");
      return false;
    }
    setFromDate(toApiDate(start));
    setToDate(toApiDate(end));
    setCurrentPage(1);
    return true;
  };

  const handleClearDates = () => {
    setPickerRange([null, null]);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const clearListingFilters = () => {
    handleClearDates();
    setTransactionType(ALL_TYPES);
    setCurrentPage(1);
  };

  const hasAnyListingFilter = Boolean(
    hasDateRangeSelected ||
      (transactionType && transactionType !== ALL_TYPES) ||
      pickerRange[0] ||
      pickerRange[1]
  );

  const handleExportCsv = useCallback(async () => {
    if (!driverId) return;

    setExporting(true);
    trackExport(exportKey);
    try {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        clearExport(exportKey);
        setExporting(false);
        return;
      }

      const exportType =
        transactionType && transactionType !== ALL_TYPES ? transactionType : undefined;
      const payload = await requestDriverLedgerCsvExport({
        driverId,
        fromDate: hasBothDates ? fromDate : "",
        toDate: hasBothDates ? toDate : "",
        type: exportType,
        token,
      });

      if (hasCsvLinkInResponse(payload)) {
        finishExport(exportKey, { payload });
        return;
      }
    } catch (err) {
      console.error(err);
      clearExport(exportKey);
      setExporting(false);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : err?.message || "Could not submit export request."
      );
    }
  }, [
    driverId,
    exportKey,
    fromDate,
    toDate,
    hasBothDates,
    transactionType,
    trackExport,
    clearExport,
    finishExport,
  ]);

  const handleDownloadCsvLog = useCallback(async (row) => {
    if (!isCsvLogDownloadReady(row)) {
      toast.info(
        row?.statusLabel === "Failed"
          ? "CSV export failed. Please try exporting again."
          : "File is not available for download yet."
      );
      return;
    }

    setDownloadingLogId(row.id);
    try {
      const result = await downloadCsvLogFile(row);
      if (result?.blob) {
        downloadBlobFile(result.filename, result.blob);
      } else if (result === null) {
        toast.info("Opening download in a new tab.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : err?.message || "Could not download file."
      );
    } finally {
      setDownloadingLogId("");
    }
  }, []);

  return (
    <div className="tab-pane-content mt-7 font-UbuntuRegular" style={{}}>
      <div className="flex justify-start">
        <Link to={backToList} className="btn btn-primary">
          Back
        </Link>
      </div>

      <div className="flex flex-wrap gap-3 mt-5 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("listing")}
          className="px-6 py-2.5 text-sm font-UbuntuMedium rounded-md border"
          style={{
            borderColor: C.border,
            background: activeTab === "listing" ? C.primary : "#fff",
            color: activeTab === "listing" ? "#fff" : C.primary,
          }}
        >
          Listing
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("logCsv")}
          className="px-6 py-2.5 text-sm font-UbuntuMedium rounded-md border"
          style={{
            borderColor: C.border,
            background: activeTab === "logCsv" ? C.primary : "#fff",
            color: activeTab === "logCsv" ? "#fff" : C.primary,
          }}
        >
          Log CSV
        </button>
      </div>

      <div
        className="rounded-md border overflow-hidden bg-white mb-4"
        style={{ borderColor: C.border }}
      >
        <div className="px-6 py-4 flex flex-wrap gap-x-8 gap-y-2">
          <p className="text-[18px] leading-none font-UbuntuMedium" style={{ color: C.text }}>
            Driver:{" "}
            <span className="text-gray-600 font-UbuntuRegular text-[16px]">
              {loading ? "Loading…" : driverFullName}
            </span>
          </p>
          <p className="text-[18px] leading-none font-UbuntuMedium" style={{ color: C.text }}>
            Current Balance:{" "}
            <span className="text-gray-600 font-UbuntuRegular text-[16px]">
              {loading ? "—" : formatINR(currentBalance)}
            </span>
          </p>
        </div>
        <div
          className="border-t px-6 py-3 flex flex-wrap gap-8 text-sm"
          style={{ borderColor: C.border }}
        >
          <p style={{ color: C.muted }}>
            Salary{" "}
            <span className="ml-2 font-UbuntuMedium text-gray-700">
              {loading ? "—" : formatINR(totalSalary)}
            </span>
          </p>
          <p style={{ color: C.muted }}>
            Award{" "}
            <span className="ml-2 font-UbuntuMedium text-gray-700">
              {loading ? "—" : formatINR(totalAward)}
            </span>
          </p>
        </div>
      </div>

      {activeTab === "listing" ? (
        <>
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="shrink-0">
          <label className="text-xs font-UbuntuMedium block mb-1.5" style={{ color: C.muted }}>
            Date range
          </label>
          <div className="inline-flex h-11 flex-nowrap items-center gap-2.5">
            <DateRangeFilter
              variant="popover"
              bare
              toolbarIcon
              range={pickerRange}
              onRangeChange={setPickerRange}
              onApply={handleApplyDates}
              onClear={handleClearDates}
              maxDate={new Date()}
            />
            {hasDateRangeSelected && (
              <DateRangeAppliedLabel
                from={fromDate}
                to={toDate}
                prefix="Date range: "
                className="shrink-0 whitespace-nowrap"
              />
            )}
          </div>
        </div>
        {hasAnyListingFilter ? (
          <button
            type="button"
            onClick={clearListingFilters}
            className="h-11 shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
          >
            Clear Filters
          </button>
        ) : null}
        <div className="min-w-[200px]">
          <label className="text-xs font-UbuntuMedium block mb-1.5" style={{ color: C.muted }}>
            Transition Type
          </label>
          <select
            className="form-control h-11 rounded-lg border"
            style={{ borderColor: C.border }}
            value={transactionType}
            disabled={typesLoading}
            onChange={(e) => {
              setTransactionType(e.target.value);
              setCurrentPage(1);
            }}
          >
            {typesLoading ? (
              <option value={ALL_TYPES}>Loading types…</option>
            ) : (
              transactionTypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))
            )}
          </select>
        </div>
        <button
          type="button"
          onClick={handleExportCsv}
          disabled={!canExport}
          className="ml-auto h-11 px-8 rounded-md text-white font-UbuntuMedium !bg-primary"
          style={{
            background: C.primary,
            opacity: canExport ? 1 : 0.5,
            cursor: canExport ? "pointer" : "not-allowed",
          }}
        >
          {exportInProgress ? "Exporting..." : "Export"}
        </button>
      </div>

      {dateValidationError ? (
        <p className="mb-3 text-sm text-red-500">{dateValidationError}</p>
      ) : null}

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="table-responsive m-0">
          <table className="w-full mb-0 bg-white">
            <thead>
              <tr className="text-center" style={{ background: C.tableHead }}>
                {[
                  "S.No.",
                  "Date & Time",
                  "Type",
                  "Trip ID",
                  "Credit +",
                  "Debit -",
                  "Running Balance",
                  "Description",
                ].map((title) => (
                  <th
                    key={title}
                    className="text-[13px] font-UbuntuMedium text-white whitespace-nowrap px-3 py-3.5 border-0"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-10" style={{ color: C.muted }}>
                    Loading ledger…
                  </td>
                </tr>
              ) : currentRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10" style={{ color: C.muted }}>
                    No transactions found.
                  </td>
                </tr>
              ) : (
                currentRows.map((row, i) => (
                  <tr key={row.id} className={i % 2 ? "bg-[#FAFAFA] text-center" : "bg-white text-center"}>
                    <td className="px-3 py-4 text-center">{String(firstIndex + i + 1).padStart(2, "0")}</td>
                    <td className="px-3 py-4 whitespace-nowrap">{row.dateTime}</td>
                    <td className="px-3 py-4 text-center">{row.verificationType}</td>
                    <td className="px-3 py-4 text-center">{row.tripId}</td>
                    <td className="px-3 py-4 text-center">{row.credit}</td>
                    <td className="px-3 py-4 text-center">{row.debit}</td>
                    <td className="px-3 py-4 text-center">{row.runningBalance}</td>
                    <td className="px-3 py-4 min-w-[260px] max-w-[360px] whitespace-pre-wrap break-words text-center leading-relaxed">
                      {row.description}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-5 text-lg">
        <span style={{ color: C.muted }}>
          {totalRecords.toLocaleString("en-IN")} records found
        </span>
        <button
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || !canPaginate}
          className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
        >
          <img src={LeftAngle} className="invert grayscale brightness-100 h-3" alt="" />
        </button>
        <PageNumberInput
          page={page}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          disabled={!canPaginate}
          style={{ borderColor: C.border, color: C.text }}
        />
        <span style={{ color: C.muted }}>of {totalPages}</span>
        <button
          type="button"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages || !canPaginate}
          className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
        >
          <img src={RightAngle} className="invert grayscale brightness-100 h-3" alt="" />
        </button>
      </div>
      </>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
          <div className="table-responsive m-0">
            <table className="w-full mb-0 bg-white">
              <thead>
                <tr className="text-center" style={{ background: C.tableHead }}>
                  {["S.No.", "File Name", "Filters", "Created Date", "Status", "Action"].map((title) => (
                    <th
                      key={title}
                      className="text-[13px] font-UbuntuMedium text-white whitespace-nowrap px-3 py-3.5 border-0"
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvLogLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10" style={{ color: C.muted }}>
                      Loading CSV log…
                    </td>
                  </tr>
                ) : csvLogRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10" style={{ color: C.muted }}>
                      No CSV log entries found.
                    </td>
                  </tr>
                ) : (
                  csvLogRows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 ? "bg-[#FAFAFA] text-center" : "bg-white text-center"}
                    >
                      <td className="px-3 py-4">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-3 py-4 max-w-[240px] truncate" title={row.fileName}>
                        {row.fileName}
                      </td>
                      <td className="px-3 py-4 max-w-[280px] whitespace-normal text-center" title={row.filters}>
                        {row.filters || "—"}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">{row.createdAt}</td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <CsvLogStatusBadge statusLabel={row.statusLabel} />
                      </td>
                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => handleDownloadCsvLog(row)}
                          disabled={!isCsvLogDownloadReady(row) || downloadingLogId === row.id}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: C.tableHead }}
                          title="Download"
                          aria-label="Download"
                        >
                          <img src={DownloadIcon} alt="" className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalaryAwardsDetail;
